(() => {
  if (window.__pumiiiUniversal) return;
  window.__pumiiiUniversal = true;

  const rootSelector = ":is(main[data-template$='pumiii-universal'], main[data-template$='pumiii-test-product-mark'])";
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-pumiii-visible');
        revealObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -9% 0px', threshold: 0.08 }
  );

  const revealSelectors = [
    '.pu-section-head',
    '.pu-why__solo',
    '.pu-why__feature',
    '.pu-why__card',
    '.pu-accordion',
    '.pu-guide__copy',
    '.pu-guide__media',
    '.pu-compare',
    '.pu-demo-grid',
    '.pu-app-host',
    '.pu-ugc__rail',
    '.pu-review-summary',
    '.pu-reviews__content',
    '.pu-details__shell',
    '.pu-card',
    '.pu-final__card',
    '.pu-brand-world__stage',
    '.pu-brand-world__copy',
  ].join(',');

  const maskSelectors = [
    '.product-information__media',
    '.pu-why__solo-media',
    '.pu-why__feature-media',
    '.pu-why__card-media',
    '.pu-guide__media',
    '.pu-demo-grid__item',
    '.pu-ugc-card__media',
    '.pu-brand-world__media',
  ].join(',');

  const observeReveal = (node, index = 0) => {
    if (node.dataset.puObserved === 'true') return;
    node.dataset.puObserved = 'true';
    node.dataset.pumiiiReveal = '';
    node.style.setProperty('--pumiii-delay', `${Math.min(index % 4, 3) * 90}ms`);
    if (reducedMotion.matches || node.getBoundingClientRect().top < window.innerHeight * 0.88) {
      node.classList.add('is-pumiii-visible');
      return;
    }
    revealObserver.observe(node);
  };

  const setupMotion = (root) => {
    root.querySelectorAll(revealSelectors).forEach(observeReveal);
    root.querySelectorAll(maskSelectors).forEach((node, index) => {
      node.dataset.puMask = '';
      observeReveal(node, index);
    });
    requestAnimationFrame(() => root.classList.add('is-pu-motion-ready'));
  };

  const setupProductDepth = (root) => {
    const stage = root.querySelector('.product-information__media');
    if (!stage || stage.dataset.puDepthReady === 'true') return;
    stage.dataset.puDepthReady = 'true';

    const reset = () => {
      stage.style.setProperty('--pu-photo-x', '0px');
      stage.style.setProperty('--pu-photo-y', '0px');
    };

    stage.addEventListener('pointermove', (event) => {
      if (!finePointer.matches || reducedMotion.matches) return;
      const bounds = stage.getBoundingClientRect();
      const x = (event.clientX - bounds.left) / bounds.width - 0.5;
      const y = (event.clientY - bounds.top) / bounds.height - 0.5;
      stage.style.setProperty('--pu-photo-x', `${x * 7}px`);
      stage.style.setProperty('--pu-photo-y', `${y * 7}px`);
    });
    stage.addEventListener('pointerleave', reset);
  };

  const setupAccordions = (root) => {
    root.querySelectorAll('.pu-accordion__row').forEach((details) => {
      if (details.dataset.puAccordionReady === 'true') return;
      details.dataset.puAccordionReady = 'true';
      const panel = details.querySelector('.pu-accordion__panel');
      if (!panel) return;
      details.addEventListener('toggle', () => {
        if (!details.open || reducedMotion.matches) return;
        panel.animate(
          [
            { opacity: 0, transform: 'translateY(-8px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
      });
    });
  };

  const setupVariantFeedback = (root) => {
    const picker = root.querySelector('variant-picker');
    if (!picker || picker.dataset.puVariantReady === 'true') return;
    picker.dataset.puVariantReady = 'true';
    picker.addEventListener('change', () => {
      root.classList.remove('is-pu-variant-changing');
      requestAnimationFrame(() => root.classList.add('is-pu-variant-changing'));
      window.setTimeout(() => root.classList.remove('is-pu-variant-changing'), 620);
      window.setTimeout(() => syncStickyPurchase(root), 80);
      window.setTimeout(() => syncStickyPurchase(root), 420);
    });
  };

  const syncStickyPurchase = (root) => {
    const finalButton = root.querySelector('.pu-final [data-pu-add]');
    if (!finalButton) return;
    const variantInput = root.querySelector('.product-information form[action*="/cart/add"] input[name="id"]');
    const variantId = Number(variantInput?.value);
    if (variantId) finalButton.dataset.puAdd = String(variantId);
  };

  const setupStickyPurchase = (root) => {
    if (root.dataset.puStickyReady === 'true') return;
    const primaryButton =
      root.querySelector('product-form-component[data-pu-bundle-product="true"] .add-to-cart-button') ||
      root.querySelector('.product-information product-form-component .add-to-cart-button') ||
      root.querySelector('.product-information .add-to-cart-button');
    const sticky = root.querySelector('.pu-final');
    if (!primaryButton || !sticky) return;
    root.dataset.puStickyReady = 'true';
    const phoneViewport = window.matchMedia(
      '(max-width: 749px), (max-width: 950px) and (max-height: 500px) and (pointer: coarse)'
    );
    const supportsOfferBar = sticky.dataset.puBundleFinal === 'true';
    const pageScroller = root.closest('.page-wrapper');
    const currentScrollTop = () =>
      Math.max(
        window.scrollY,
        document.scrollingElement?.scrollTop || 0,
        pageScroller?.scrollTop || 0
      );
    const updateStickySpace = () => {
      if (!phoneViewport.matches) return;
      const height = Math.ceil(sticky.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty('--pu-mobile-buybar-space', `${height}px`);
      }
    };
    const refresh = () => {
      const hasStarted = currentScrollTop() > 180;
      const isSuppressed = root.dataset.puStickySuppressed === 'true';
      const shouldShow = hasStarted && !isSuppressed && supportsOfferBar;
      root.classList.toggle('is-pu-mobile-buybar-visible', shouldShow);
      if (shouldShow) requestAnimationFrame(updateStickySpace);
    };
    const resizeObserver = new ResizeObserver(updateStickySpace);
    resizeObserver.observe(sticky);
    phoneViewport.addEventListener('change', refresh);
    pageScroller?.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh, { passive: true });
    syncStickyPurchase(root);
    refresh();
  };

  const setupAdaptiveTitle = (root) => {
    const title = root.querySelector('[data-pu-product-title], .product-details .text-block h1, .product-details .text-block h2, .product-details .text-block p[role="heading"]');
    if (!title) return;
    const length = title.textContent.trim().length;
    root.classList.toggle('is-pu-title-long', length > 42);
    root.classList.toggle('is-pu-title-xlong', length > 72);
  };

  const setupStoryMotion = (root) => {
    if (root.dataset.puStoryReady === 'true') return;
    const cards = Array.from(root.querySelectorAll('[data-pu-stack-card]'));
    if (!cards.length) return;
    root.dataset.puStoryReady = 'true';
    cards.forEach((card, index) => {
      card.style.setProperty('--pumiii-delay', `${index * 110}ms`);
      observeReveal(card, index);
    });
  };

  const sanitizeVisibleCopy = (root) => {
    if (root.dataset.puCopyReady === 'true') return;
    root.dataset.puCopyReady = 'true';
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      if (node.nodeValue.includes('—') || node.nodeValue.includes('–')) {
        node.nodeValue = node.nodeValue.replace(/\s*—\s*/g, ', ').replace(/\s*–\s*/g, ' - ');
      }
      node = walker.nextNode();
    }
  };

  const cleanDescriptionArtifacts = (root) => {
    if (root.dataset.puDescriptionReady === 'true') return;
    root.dataset.puDescriptionReady = 'true';
    const title = root.querySelector('[data-pu-product-title]');
    const originalTitle = title?.dataset.puOriginalTitle?.trim();
    root.querySelectorAll('.pu-accordion__content .pu-rte').forEach((rte) => {
      [...rte.children].forEach((child) => {
        if (child.tagName === 'P' && !child.textContent.trim()) child.remove();
      });
      const firstList = rte.querySelector(':scope > ul:first-of-type');
      if (firstList) {
        const labels = [...firstList.querySelectorAll(':scope > li > strong')].map((node) => node.textContent.trim().toLowerCase());
        const generatorLabels = ['subheading:', 'benefit bullets:', 'cta:', 'trust line:'];
        if (labels.length >= 2 && labels.every((label) => generatorLabels.includes(label))) firstList.remove();
      }
      if (!originalTitle) return;
      const firstParagraph = rte.querySelector(':scope > p');
      if (!firstParagraph?.textContent.trim().startsWith(originalTitle)) return;
      firstParagraph.textContent = firstParagraph.textContent.trim().slice(originalTitle.length).trim();
      if (!firstParagraph.textContent) firstParagraph.remove();
    });
  };

  const setup = (scope = document) => {
    const roots = [];
    if (scope.matches?.(rootSelector)) roots.push(scope);
    const descendants = scope.querySelectorAll ? scope.querySelectorAll(rootSelector) : [];
    roots.push(...descendants);
    for (const root of roots) {
      setupMotion(root);
      setupProductDepth(root);
      setupAccordions(root);
      setupVariantFeedback(root);
      setupAdaptiveTitle(root);
      cleanDescriptionArtifacts(root);
      sanitizeVisibleCopy(root);
      setupStoryMotion(root);
      setupStickyPurchase(root);
    }
  };

  const cartRoot = () => window.Shopify?.routes?.root || '/';

  const markDirectCheckout = (target) => {
    if (!(target instanceof Element)) return;
    const productForm = target.closest('product-form-component[data-pu-direct-checkout="true"]');
    if (!productForm) return;
    const root = productForm.closest(rootSelector);
    if (root) root.dataset.puCheckoutPending = 'true';
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('.add-to-cart-button') : null;
      if (!button || button.disabled) return;
      markDirectCheckout(button);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => markDirectCheckout(event.target),
    true
  );

  document.addEventListener('shopify:cart:lines-update', (event) => {
    const productForm =
      event.target instanceof Element
        ? event.target.closest('product-form-component[data-pu-direct-checkout="true"]')
        : null;
    const root = productForm?.closest(rootSelector);
    if (!root || root.dataset.puCheckoutPending !== 'true' || !(event.promise instanceof Promise)) return;

    event.promise
      .then((result) => {
        if (result?.detail?.didError) {
          delete root.dataset.puCheckoutPending;
          return;
        }
        root.dataset.puCheckoutPending = 'redirecting';
        window.setTimeout(() => window.location.assign(`${cartRoot()}checkout`), 120);
      })
      .catch(() => delete root.dataset.puCheckoutPending);
  });

  const findBundleOffer = (root) => {
    const candidates = root.querySelectorAll('h1, h2, h3, h4, h5, h6, legend, p, span, div');
    const heading = [...candidates].find(
      (node) => node.textContent.trim().replace(/\s+/g, ' ').toLowerCase() === 'buy more, save more'
    );
    if (!heading) return null;
    return (
      heading.closest(
        '.bx-widget, [data-bundlex], [data-bundle], [class*="bundlex" i], [id*="bundlex" i], [class*="bundle" i], [id*="bundle" i]'
      ) || heading
    );
  };

  const notifyCartUpdated = (cart, sourceId) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId,
          data: { source: 'product-form-component', itemCount: cart.item_count },
        },
      })
    );
  };

  document.addEventListener('click', (event) => {
    const offerButton = event.target.closest('[data-pu-bundle-scroll]');
    if (offerButton) {
      const root = offerButton.closest(rootSelector);
      if (!root) return;
      const target = findBundleOffer(root) || root.querySelector('.product-information product-form-component');
      if (!target) return;
      root.dataset.puStickySuppressed = 'true';
      root.classList.remove('is-pu-mobile-buybar-visible');
      target.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'center',
      });
      window.setTimeout(() => {
        delete root.dataset.puStickySuppressed;
        window.dispatchEvent(new Event('scroll'));
      }, reducedMotion.matches ? 250 : 1500);
      return;
    }

    const button = event.target.closest('[data-pu-add]');
    if (!button || button.dataset.puState === 'loading') return;
    const variantId = Number(button.dataset.puAdd);
    if (!variantId) return;

    button.dataset.puState = 'loading';
    fetch(`${cartRoot()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('add-to-cart failed');
        return fetch(`${cartRoot()}cart.js`).then((cartResponse) => cartResponse.json());
      })
      .then((cart) => {
        button.dataset.puState = 'added';
        notifyCartUpdated(cart, button.id || 'pumiii-universal');
        window.setTimeout(() => delete button.dataset.puState, 2400);
      })
      .catch(() => {
        delete button.dataset.puState;
        document.querySelector(`${rootSelector} product-form-component`)?.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'center',
        });
      });
  });

  class PumiiiRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '500px 0px' }
      );
      observer.observe(this);
    }

    load() {
      this.dataset.loaded = 'true';
      if (!this.dataset.url) return;
      fetch(this.dataset.url)
        .then((response) => (response.ok ? response.text() : Promise.reject()))
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const fresh = html.querySelector('.pu-container');
          if (!fresh?.querySelector('.pu-card')) return;
          this.replaceChildren(fresh);
          setup(document);
        })
        .catch(() => {});
    }
  }

  if (!customElements.get('pumiii-recommendations')) {
    customElements.define('pumiii-recommendations', PumiiiRecommendations);
  }

  const boot = () => {
    setup(document);
    const main = document.querySelector(rootSelector);
    if (!main || main.dataset.puMutationReady === 'true') return;
    main.dataset.puMutationReady = 'true';
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        setup(document);
      });
    });
    observer.observe(main, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.closest?.(rootSelector) || document.querySelector(rootSelector);
    root?._puGsapCleanup?.();
  });
  reducedMotion.addEventListener('change', () => setup(document));
})();
